import React, { useState, useRef, useEffect } from 'react'
import { SubmitButton } from '../../reusable/cta/cta'
import api from '../../../helpers/apiRequester'
import styles from './styles.module.scss'

interface Message {
    id: string
    text: string
    sender: 'user' | 'bot'
    timestamp: Date
    isLoading?: boolean
}

export const ChatbotView = () => {
    const [messages, setMessages] = useState<Message[]>([
        {
            id: '1',
            text: "Hello! I'm your assistant. How can I help you today?",
            sender: 'bot',
            timestamp: new Date(),
        },
    ])
    const [inputValue, setInputValue] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState('')
    const messagesEndRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    // Auto-scroll to bottom when new messages are added
    useEffect(() => {
        scrollToBottom()
    }, [messages])

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }

    const generateMessageId = () => {
        return Date.now().toString() + Math.random().toString(36).substr(2, 9)
    }

    const sendMessage = async () => {
        if (!inputValue.trim() || isLoading) return

        const userMessage: Message = {
            id: generateMessageId(),
            text: inputValue.trim(),
            sender: 'user',
            timestamp: new Date(),
        }

        // Add user message
        setMessages((prev) => [...prev, userMessage])
        setInputValue('')
        setError('')
        setIsLoading(true)

        // Add loading message
        const loadingMessage: Message = {
            id: generateMessageId(),
            text: '',
            sender: 'bot',
            timestamp: new Date(),
            isLoading: true,
        }
        setMessages((prev) => [...prev, loadingMessage])

        try {
            // Send request to backend
            const response = await api.post('/chatbot-query', {
                query: userMessage.text,
                sessionId: sessionStorage.getItem('chatSessionId'),
            })

            const queryResponse = response.data.results
            console.log('queryResponse', queryResponse)
            // Remove loading message and add bot response
            setMessages((prev) => {
                const withoutLoading = prev.filter((msg) => !msg.isLoading)
                const botResponse: Message = {
                    id: queryResponse.sessionId,
                    text:
                        queryResponse.completion ||
                        "I apologize, but I didn't understand that. Could you please rephrase your question?",
                    sender: 'bot',
                    timestamp: new Date(),
                }
                return [...withoutLoading, botResponse]
            })

            // Store session ID for conversation continuity
            if (queryResponse.sessionId) {
                sessionStorage.setItem('chatSessionId', queryResponse.sessionId)
            }
        } catch (error: any) {
            console.error('Chatbot error:', error)

            // Remove loading message and add error message
            setMessages((prev) => {
                const withoutLoading = prev.filter((msg) => !msg.isLoading)
                const errorMessage: Message = {
                    id: generateMessageId(),
                    text: "I'm sorry, I'm having trouble connecting right now. Please try again in a moment.",
                    sender: 'bot',
                    timestamp: new Date(),
                }
                return [...withoutLoading, errorMessage]
            })

            setError('Failed to send message. Please try again.')
        } finally {
            setIsLoading(false)
            inputRef.current?.focus()
        }
    }

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            sendMessage()
        }
    }

    const formatTime = (date: Date) => {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }

    const clearChat = () => {
        setMessages([
            {
                id: '1',
                text: "Hello! I'm your assistant. How can I help you today?",
                sender: 'bot',
                timestamp: new Date(),
            },
        ])
        sessionStorage.removeItem('chatSessionId')
        setError('')
    }

    return (
        <div className={styles.chatbotView}>
            <div className={styles.chatbotContainer}>
                <div className={styles.chatHeader}>
                    <h2>AI Assistant</h2>
                    <button className={styles.clearButton} onClick={clearChat} title="Clear conversation">
                        Clear Chat
                    </button>
                </div>

                <div className={styles.messagesContainer}>
                    <div className={styles.messagesList}>
                        {messages.map((message) => (
                            <div
                                key={message.id}
                                className={`${styles.messageWrapper} ${
                                    message.sender === 'user' ? styles.userMessage : styles.botMessage
                                }`}
                            >
                                <div className={styles.messageContent}>
                                    {message.isLoading ? (
                                        <div className={styles.loadingDots}>
                                            <span></span>
                                            <span></span>
                                            <span></span>
                                        </div>
                                    ) : (
                                        <div className={styles.messageText}>{message.text}</div>
                                    )}
                                    <div className={styles.messageTime}>{formatTime(message.timestamp)}</div>
                                </div>
                            </div>
                        ))}
                        <div ref={messagesEndRef} />
                    </div>
                </div>

                <div className={styles.inputContainer}>
                    {error && <div className={styles.errorMessage}>{error}</div>}
                    <div className={styles.inputWrapper}>
                        <input
                            ref={inputRef}
                            type="text"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Type your message here..."
                            className={styles.messageInput}
                            disabled={isLoading}
                            maxLength={1000}
                        />
                        <SubmitButton
                            text={isLoading ? 'Sending...' : 'Send'}
                            btnType="primary"
                            onClick={sendMessage}
                        />
                    </div>
                    <div className={styles.inputHelp}>Press Enter to send • Shift+Enter for new line</div>
                </div>
            </div>
        </div>
    )
}
